import { useEffect, useMemo, useState } from 'react';
import { Check, RotateCcw, X } from 'lucide-react';

// Nur „Blatt"-Textelemente sind anklickbar (Elemente ohne Kind-Elemente, reiner Text).
const CLICK_SELECTOR = 'h1,h2,h3,h4,h5,h6,span,b,strong,small,label,button,a,p,td,th,li,em,legend,summary,dt,dd,figcaption';

interface Editor { readonly original: string; readonly value: string; }

/**
 * Klick-zum-Umbenennen-Modus für freie Texte im Inhaltsbereich. Ist er aktiv, öffnet ein Klick auf ein
 * Text-Element (Überschrift, Label, Button …) einen kleinen Dialog; der Ersatz gilt mandantenweit überall,
 * wo genau dieser Text vorkommt. Nur innerhalb von `.forq-content` (Shell/Navigation bleiben unberührt).
 */
export function TextRenameLayer({ active, overrides, onRename }: {
  readonly active: boolean;
  readonly overrides: ReadonlyMap<string, string>;
  readonly onRename: (original: string, replacement: string | null) => void;
}) {
  const [editor, setEditor] = useState<Editor | null>(null);
  // Ersatz→Original, um beim erneuten Anklicken eines schon umbenannten Textes den Original-Key zu finden.
  const reverse = useMemo(() => {
    const map = new Map<string, string>();
    for (const [key, value] of overrides) map.set(value, key);
    return map;
  }, [overrides]);

  useEffect(() => {
    if (!active) return;
    document.body.classList.add('forq-text-editing');
    const onClick = (event: MouseEvent) => {
      const target = event.target as HTMLElement | null;
      if (!target || !target.closest('.forq-content')) return;
      const el = target.closest<HTMLElement>(CLICK_SELECTOR);
      if (!el || el.children.length > 0) return;
      const text = (el.textContent ?? '').trim();
      if (!text) return;
      event.preventDefault();
      event.stopPropagation();
      const original = reverse.get(text) ?? text;
      setEditor({ original, value: overrides.get(original) ?? original });
    };
    document.addEventListener('click', onClick, true);
    return () => {
      document.body.classList.remove('forq-text-editing');
      document.removeEventListener('click', onClick, true);
    };
  }, [active, overrides, reverse]);

  if (!active || !editor) return null;

  const save = () => {
    const trimmed = editor.value.trim();
    onRename(editor.original, trimmed && trimmed !== editor.original ? trimmed : null);
    setEditor(null);
  };

  return (
    <div className="forq-terms" role="dialog" aria-modal="true" aria-label="Text umbenennen" onClick={() => setEditor(null)}>
      <div className="forq-terms__panel forq-textedit__panel" onClick={(event) => event.stopPropagation()}>
        <header className="forq-terms__head">
          <div><span className="forq-eyebrow">Mandant · Text</span><h2>Text umbenennen</h2></div>
          <button type="button" className="forq-terms__close" onClick={() => setEditor(null)} aria-label="Schließen"><X size={18} /></button>
        </header>
        <p className="forq-terms__lead">Ersetzt „{editor.original}" überall, wo genau dieser Text vorkommt. Feld leeren = Standard.</p>
        <div className="forq-textedit__field">
          <input
            autoFocus
            value={editor.value}
            placeholder={editor.original}
            aria-label="Neuer Text"
            onChange={(event) => setEditor({ ...editor, value: event.target.value })}
            onKeyDown={(event) => { if (event.key === 'Enter') save(); if (event.key === 'Escape') setEditor(null); }}
          />
        </div>
        <footer className="forq-terms__foot">
          <button type="button" className="forq-terms__reset" onClick={() => { onRename(editor.original, null); setEditor(null); }}><RotateCcw size={14} /> Standard</button>
          <div className="forq-terms__foot-actions">
            <button type="button" className="forq-terms__cancel" onClick={() => setEditor(null)}>Abbrechen</button>
            <button type="button" className="forq-terms__save" onClick={save}><Check size={14} /> Übernehmen</button>
          </div>
        </footer>
      </div>
    </div>
  );
}
